/* Canko Lab theme — storefront behaviour. Vanilla JS, no build step. */
(() => {
  "use strict";

  const moneyFormat = (cents, format) => {
    const value = (Math.abs(cents) / 100).toFixed(2);
    return (format || window.CankoTheme?.moneyFormat || "${{amount}}").replace(
      /\{\{\s*amount\s*\}\}/,
      value
    );
  };

  // Cart line variant name — same rule as snippets/variant-label.liquid: a single
  // piece (Style that isn't "Set of …") leaves out the second piece's colour.
  const variantLabel = (item) => {
    const opts = item.options_with_values || [];
    if (!opts.length) return item.variant_title;
    const single = opts.some((o) => o.name.toLowerCase() === "style" && !/set of/i.test(o.value));
    let colourSeen = 0;
    return opts
      .filter((o) => {
        if (!/colou?r/i.test(o.name)) return true;
        colourSeen += 1;
        return !(single && colourSeen > 1);
      })
      .map((o) => o.value)
      .join(" / ");
  };

  /* ---------------------------------------------------------------------
     Quantity steppers — product form, cart drawer lines, anywhere.
     Markup: <div class="qty-stepper" data-qty-stepper>
               <button data-qty-decrease>−</button>
               <input type="number" data-qty-input min="1">
               <button data-qty-increase>+</button>
             </div>
  --------------------------------------------------------------------- */
  function initQuantitySteppers(root = document) {
    root.querySelectorAll("[data-qty-stepper]").forEach((stepper) => {
      if (stepper.dataset.bound) return;
      stepper.dataset.bound = "true";
      const input = stepper.querySelector("[data-qty-input]");
      const dec = stepper.querySelector("[data-qty-decrease]");
      const inc = stepper.querySelector("[data-qty-increase]");
      const min = parseInt(input.min || "1", 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const commit = () => stepper.dispatchEvent(new CustomEvent("qty:change", { bubbles: true, detail: { value: parseInt(input.value, 10) || min } }));
      dec?.addEventListener("click", () => {
        input.value = Math.max(min, (parseInt(input.value, 10) || min) - 1);
        commit();
      });
      inc?.addEventListener("click", () => {
        input.value = Math.min(max, (parseInt(input.value, 10) || min) + 1);
        commit();
      });
      input?.addEventListener("change", () => {
        input.value = Math.min(max, Math.max(min, parseInt(input.value, 10) || min));
        commit();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Cart drawer / AJAX cart
  --------------------------------------------------------------------- */
  class CankoCart {
    constructor() {
      this.drawer = document.querySelector("[data-cart-drawer]");
      this.body = document.querySelector("[data-cart-drawer-body]");
      this.countEls = document.querySelectorAll("[data-cart-count]");
      this.subtotalEls = document.querySelectorAll("[data-cart-subtotal]");
      this.shipFillEls = document.querySelectorAll("[data-free-shipping-fill]");
      this.shipTextEls = document.querySelectorAll("[data-free-shipping-text]");
      this.shipGoalDotEls = document.querySelectorAll("[data-free-shipping-goal-dot]");
      this.isUsd = document.body.dataset.currencyCode === "USD";
      this.threshold = this.isUsd
        ? parseFloat(document.body.dataset.freeShippingThresholdUsd || "0") * 100
        : parseFloat(document.body.dataset.freeShippingThreshold || "0") * 100;
      this.currencyPrefix = this.isUsd ? "US$ " : "CA$ ";
      this.bindGlobal();
    }

    bindGlobal() {
      document.addEventListener("click", (e) => {
        const opener = e.target.closest("[data-cart-drawer-open]");
        if (opener) {
          e.preventDefault();
          this.open();
        }
        const closer = e.target.closest("[data-cart-drawer-close]");
        if (closer) {
          e.preventDefault();
          this.close();
        }
        const remove = e.target.closest("[data-cart-remove-line]");
        if (remove) {
          e.preventDefault();
          this.changeLine(remove.dataset.cartRemoveLine, 0);
        }
      });

      document.addEventListener("qty:change", (e) => {
        const stepper = e.target.closest("[data-cart-line-qty]");
        if (stepper) this.changeLine(stepper.dataset.cartLineQty, e.detail.value);
      });

      document.querySelectorAll("[data-product-form]").forEach((form) => {
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          this.addFromForm(form);
        });
      });

      this.drawer?.querySelector(".cart-drawer__overlay")?.addEventListener("click", () => this.close());
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.close();
      });
    }

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add("is-open");
      this.drawer.removeAttribute("hidden");
      document.body.style.overflow = "hidden";
    }
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove("is-open");
      document.body.style.overflow = "";
      setTimeout(() => this.drawer.setAttribute("hidden", "hidden"), 220);
    }

    async addFromForm(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn?.setAttribute("disabled", "disabled");
      try {
        const formData = new FormData(form);
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        const data = await res.json();
        if (data.status) {
          this.showError(form, data.description || data.message);
          return;
        }
        await this.refresh();
        if (document.body.dataset.cartType !== "page") this.open();
      } catch (err) {
        this.showError(form, "Something went wrong adding this to your bag.");
      } finally {
        submitBtn?.removeAttribute("disabled");
      }
    }

    showError(form, message) {
      let el = form.querySelector("[data-form-error]");
      if (!el) {
        el = document.createElement("p");
        el.dataset.formError = "true";
        el.style.color = "var(--status-danger)";
        el.style.fontSize = "20px";
        form.appendChild(el);
      }
      el.textContent = message || "This item is unavailable.";
    }

    async changeLine(lineKey, quantity) {
      // Shopify's /cart/change.js takes either `line` (a 1-based position
      // in the cart) or `id` (the actual line-item key/variant id, as a
      // string). We only ever have the key (item.key, e.g.
      // "40123456789:abcdef..."), so this must be sent as `id` — sending it
      // as `line` gets silently mis-parsed as an unrelated number (its
      // leading digits), targeting the wrong line or none at all, which is
      // exactly what caused removals/quantity changes to silently fail and
      // the subtotal to render as "$NaN" from the resulting error response.
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      const cart = await res.json();
      if (!res.ok || typeof cart.total_price === "undefined") {
        // Something still went wrong (e.g. sold out) — re-sync from the
        // real cart instead of rendering whatever error shape came back.
        await this.refresh();
        return;
      }
      this.render(cart);
      // The full cart page is server-rendered (line prices, subtotal), so
      // reload it to reflect the change instead of patching it in place.
      if (document.querySelector("[data-cart-page]")) window.location.reload();
    }

    async refresh() {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      this.render(cart);
      return cart;
    }

    render(cart) {
      this.countEls.forEach((el) => (el.textContent = cart.item_count));
      this.subtotalEls.forEach((el) => (el.textContent = moneyFormat(cart.total_price)));
      document.querySelectorAll("[data-cart-empty]").forEach((el) => {
        el.hidden = cart.item_count > 0;
      });
      document.querySelectorAll("[data-cart-has-items]").forEach((el) => {
        el.hidden = cart.item_count === 0;
      });

      if (this.threshold > 0) {
        const remaining = Math.max(0, this.threshold - cart.total_price);
        const pct = Math.min(100, (cart.total_price / this.threshold) * 100);
        this.shipFillEls.forEach((el) => (el.style.width = pct + "%"));
        this.shipTextEls.forEach((el) => {
          el.textContent =
            remaining > 0
              ? el.dataset.progressTemplate?.replace("__AMOUNT__", moneyFormat(remaining, this.currencyPrefix + "{{amount}}"))
              : el.dataset.unlockedText || el.textContent;
        });
        this.shipGoalDotEls.forEach((el) => el.classList.toggle("is-reached", remaining <= 0));
      }

      if (this.body) {
        const items = cart.items || [];
        this.body.innerHTML = items.length
          ? items.map((item) => this.lineHtml(item)).join("")
          : `<p class="cart-drawer__empty" data-cart-empty>${document.body.dataset.cartEmptyText || "Your bag is empty."}</p>`;
        initQuantitySteppers(this.body);
      }
    }

    lineHtml(item) {
      const img = item.image ? item.image.replace(/(\.[a-z]{3,4})(\?.*)?$/i, "_160x$1$2") : "";
      return `
        <div class="cart-line" data-line-key="${item.key}">
          <a class="cart-line__image" href="${item.url}">
            ${img ? `<img src="${img}" alt="${item.title}" loading="lazy">` : ""}
          </a>
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:6px">
            <a class="cart-line__title" href="${item.url}">${item.product_title}</a>
            ${item.variant_title ? `<div class="cart-line__variant">${variantLabel(item)}</div>` : ""}
            <div class="qty-stepper" data-qty-stepper data-cart-line-qty="${item.key}" style="height:36px">
              <button type="button" data-qty-decrease aria-label="Decrease">−</button>
              <input type="number" data-qty-input value="${item.quantity}" min="0" style="width:36px">
              <button type="button" data-qty-increase aria-label="Increase">+</button>
            </div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:8px">
            <div style="font-family:var(--font-body);font-weight:700;font-size:22px">${moneyFormat(item.final_line_price)}</div>
            <button type="button" class="cart-line__remove" data-cart-remove-line="${item.key}">Remove</button>
          </div>
        </div>`;
    }
  }

  /* ---------------------------------------------------------------------
     Variant / swatch picker
  --------------------------------------------------------------------- */
  function initVariantPickers(root = document) {
    root.querySelectorAll("[data-product-form]").forEach((form) => {
      if (form.dataset.variantBound) return;
      const variantsScript = form.querySelector("[data-product-variants]");
      // Quick-add forms on product cards (collection grid, home page) don't carry
      // the full variants JSON or option selectors — they're plain "add first
      // available variant" buttons, already correct as server-rendered. Only the
      // full product-page form (with variants JSON) needs this picker logic.
      if (!variantsScript) return;
      form.dataset.variantBound = "true";
      let variants = [];
      try {
        variants = JSON.parse(variantsScript.textContent || "[]");
      } catch (e) {
        variants = [];
      }
      // The price/compare-price markup lives in the info column just above
      // <product-form>, not inside the <form> itself, so form.querySelector
      // never finds it — that silently broke live price updates on variant
      // change for every product (only visible once a variant actually had
      // a different price; colour-only variants never showed the gap).
      // Scoped to the nearest <main> so this can't accidentally grab a price
      // from an unrelated product card (e.g. in "Goes well with" below).
      const priceScope = form.closest("main") || document;
      const idInput = form.querySelector('input[name="id"]');
      const priceEl = priceScope.querySelector("[data-product-price]");
      const compareEl = priceScope.querySelector("[data-product-compare-price]");
      const submitBtn = form.querySelector('[data-add-to-cart]');
      const availabilityEl = form.querySelector("[data-product-availability]");

      const optionInputs = () =>
        Array.from(form.querySelectorAll("[data-option-selector]"));

      const currentOptions = () => optionInputs().map((el) => el.value);

      const findVariant = () => {
        // Single-variant products ("Default Title") render no option pickers at all.
        if (!optionInputs().length) return variants[0];
        const opts = currentOptions();
        return variants.find((v) => v.options.every((o, i) => o === opts[i]));
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (submitBtn) {
            submitBtn.setAttribute("disabled", "disabled");
            submitBtn.textContent = submitBtn.dataset.unavailableText || "Unavailable";
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = moneyFormat(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = moneyFormat(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (submitBtn) {
          if (variant.available) {
            submitBtn.removeAttribute("disabled");
            submitBtn.textContent = submitBtn.dataset.addText || "Add to bag";
          } else {
            submitBtn.setAttribute("disabled", "disabled");
            submitBtn.textContent = submitBtn.dataset.soldOutText || "Sold out";
          }
        }
        if (availabilityEl) availabilityEl.textContent = variant.available ? "" : (availabilityEl.dataset.soldOutText || "Sold out");

        // If this colour has its own photo assigned in the admin (Products →
        // Variants → click a colour → Image), switch the gallery to it so
        // shoppers see the actual colour, not just whatever loaded first.
        if (variant.featured_image) {
          // Gallery markup is keyed by media id, which differs from the
          // variant's image id — compare against the media id.
          const variantMediaId = variant.featured_media ? variant.featured_media.id : variant.featured_image.id;
          const galleryMain = document.querySelector("[data-gallery-main-image]");
          if (galleryMain) {
            const sep = variant.featured_image.src.includes("?") ? "&" : "?";
            galleryMain.src = variant.featured_image.src + sep + "width=900";
          }
          document.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
            const active = Number(thumb.dataset.mediaId) === variantMediaId;
            thumb.classList.toggle("is-active", active);
            // Bring the picked photo into view inside the thumb strip.
            const strip = thumb.closest("[data-gallery-thumbs]");
            if (active && strip) {
              strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2 });
            }
          });
          // Same idea for the mobile swipe carousel. Only one slide (tagged
          // data-variant-slide at render time) ever holds a colour photo —
          // the rest are the generic shots — so on colour change we mutate
          // that one slide's image/id in place rather than looking for an
          // already-matching slide, which would never exist for any colour
          // other than whichever was selected on first page load. Uses
          // scrollLeft directly rather than scrollIntoView, which some
          // mobile browsers won't apply correctly inside a horizontally-
          // scrolling container (it can try to scroll the whole page
          // instead of just the carousel).
          const variantSlide = document.querySelector("[data-variant-slide]");
          if (variantSlide) {
            const sep2 = variant.featured_image.src.includes("?") ? "&" : "?";
            const img = variantSlide.querySelector("img");
            if (img) {
              img.src = variant.featured_image.src + sep2 + "width=900";
              img.srcset = "";
            }
            variantSlide.dataset.mediaId = variantMediaId;
            const carousel = variantSlide.closest(".product-gallery__carousel");
            if (carousel) {
              carousel.scrollTo({ left: variantSlide.offsetLeft, behavior: "smooth" });
            }
          } else {
            // Products tagged "gallery-show-all" keep every photo in the
            // carousel, so there's no swappable variant slide — just scroll
            // to the slide that already holds this variant's photo.
            const matchingSlide = document.querySelector(
              `[data-gallery-slide][data-media-id="${variantMediaId}"]`
            );
            const carousel = matchingSlide?.closest(".product-gallery__carousel");
            if (matchingSlide && carousel) {
              carousel.scrollTo({ left: matchingSlide.offsetLeft, behavior: "smooth" });
            }
          }
        }

        updateComposite();
        form.dispatchEvent(new CustomEvent("variant:change", { detail: { variant }, bubbles: true }));
      };

      // Selects `value` within one option group's swatch list (used both for
      // real clicks and for keeping hidden "secondary colour" groups synced
      // to the primary colour while the "customize separately" toggle is off).
      const selectSwatchInGroup = (group, value) => {
        const swatch = group.querySelector(`[data-swatch-value="${CSS.escape(value)}"]`);
        if (!swatch) return false; // this group doesn't have that colour — leave it as-is
        group.querySelectorAll("[data-swatch-value]").forEach((s) => {
          s.classList.remove("is-selected");
          s.setAttribute("aria-pressed", "false");
        });
        swatch.classList.add("is-selected");
        swatch.setAttribute("aria-pressed", "true");
        const hidden = group.querySelector("[data-option-selector]");
        if (hidden) hidden.value = value;
        const groupLabelEl = group.querySelector("[data-selected-option-label]");
        if (groupLabelEl) groupLabelEl.textContent = value;
        return true;
      };

      const perPartToggle = form.querySelector("[data-customize-per-part]");
      const secondaryColourGroups = () => Array.from(form.querySelectorAll("[data-secondary-colour-option]"));

      // While "customize separately" is off, every secondary colour option
      // (e.g. Stencil colour) silently follows the primary one (e.g. Color),
      // so the product reads as a single uniform colour without the shopper
      // having to set each part by hand. A group with a default colour (tag
      // "secondary-default:…", e.g. Bone White stencils) uses that instead.
      const syncSecondaryColoursToPrimary = () => {
        if (perPartToggle && perPartToggle.checked) return;
        const primaryGroup = form.querySelector("[data-primary-colour-option]");
        const primaryValue = primaryGroup?.querySelector("[data-option-selector]")?.value;
        if (!primaryValue) return;
        secondaryColourGroups().forEach((group) => selectSwatchInGroup(group, group.dataset.defaultValue || primaryValue));
      };

      // Products with a non-colour option that has a "Set of …" value (e.g.
      // Style: Short / Long / Set of Two) only need per-piece colours when
      // that set is selected. For any other value the toggle is hidden and
      // every colour option follows the primary one. Products without such
      // an option are untouched.
      const setStyleSelect = Array.from(form.querySelectorAll("select.select[data-option-selector]"))
        .find((sel) => Array.from(sel.options).some((o) => /set of/i.test(o.value)));
      const updatePerPartAvailability = () => {
        if (!perPartToggle || !setStyleSelect) return;
        const active = /set of/i.test(setStyleSelect.value);
        const toggleLabel = perPartToggle.closest("label");
        if (toggleLabel) toggleLabel.style.display = active ? "" : "none";
        if (!active && perPartToggle.checked) {
          perPartToggle.checked = false;
          secondaryColourGroups().forEach((group) => group.setAttribute("hidden", "hidden"));
        }
      };

      // A "Set of …" with two different colours has no photo of its own, so
      // build the main photo from the two single-piece photos instead: the
      // first style (e.g. Short Antler) in the primary colour on the left, the
      // second style (Long Antler) in the second colour on the right. Same
      // colour on both (or any non-set style) keeps the normal variant photo.
      const updateComposite = () => {
        const hosts = [document.querySelector(".product-gallery__main"), document.querySelector("[data-variant-slide]")].filter(Boolean);
        let pieces = null;
        const primarySel = form.querySelector("[data-primary-colour-option] [data-option-selector]");
        const secondarySel = form.querySelector("[data-secondary-colour-option] [data-option-selector]");
        if (setStyleSelect && primarySel && secondarySel && /set of/i.test(setStyleSelect.value) && primarySel.value !== secondarySel.value) {
          const pieceStyles = Array.from(setStyleSelect.options).map((o) => o.value).filter((v) => !/set of/i.test(v));
          if (pieceStyles.length === 2) {
            const inputs = optionInputs();
            const styleIdx = inputs.indexOf(setStyleSelect);
            const primaryIdx = inputs.indexOf(primarySel);
            const secondaryIdx = inputs.indexOf(secondarySel);
            const pieceVariant = (style, colour) =>
              variants.find((v) => v.options[styleIdx] === style && v.options[primaryIdx] === colour && v.options[secondaryIdx] === colour);
            const first = pieceVariant(pieceStyles[0], primarySel.value);
            const second = pieceVariant(pieceStyles[1], secondarySel.value);
            if (first?.featured_image && second?.featured_image) pieces = [first.featured_image.src, second.featured_image.src];
          }
        }
        // Build-your-own sets (tag "choose-set", e.g. Knitted Ornaments "Set of 3"):
        // once at least two of the "Ornament 1/2/3" pickers are filled, show the
        // chosen designs side by side using each single design's variant photo.
        // Fewer than two chosen keeps the set's own photo (e.g. the trio shot).
        const setPicker = form.querySelector("[data-set-picker]");
        const currentVariant = findVariant();
        if (!pieces && setPicker && currentVariant && /set of/i.test(currentVariant.title)) {
          const chosen = Array.from(setPicker.querySelectorAll("select")).map((s) => s.value).filter(Boolean);
          const srcs = chosen.map((title) => variants.find((v) => v.title === title)?.featured_image?.src);
          if (srcs.length >= 2 && srcs.every(Boolean)) pieces = srcs;
        }
        hosts.forEach((host) => {
          host.querySelector(":scope > [data-composite]")?.remove();
          Array.from(host.children).forEach((el) => { if (el.tagName === "IMG") el.style.visibility = ""; });
          if (!pieces) return;
          const wrap = document.createElement("div");
          wrap.setAttribute("data-composite", "");
          wrap.style.cssText = "position:absolute;inset:0;display:flex;background:#fff";
          // Each single-piece photo is a square with the piece centred in it, so
          // each slot shows the middle of its photo (piece centred in the slot).
          // Two pieces: each half shows the middle 50% at full height. Three
          // pieces: a third is too narrow for the middle third, so each photo is
          // scaled down to show its middle 55% (whole piece, a little smaller),
          // vertically centred on the white background. The floor shadow spills
          // sideways, so every edge that meets a neighbour fades out.
          const n = pieces.length;
          pieces.forEach((src, i) => {
            const slot = document.createElement("div");
            const fadeLeft = i > 0, fadeRight = i < n - 1;
            const mask = `linear-gradient(to right,${fadeLeft ? "transparent 0," : ""}#000 ${fadeLeft ? 14 : 0}%,#000 ${fadeRight ? 86 : 100}%${fadeRight ? ",transparent 100%" : ""})`;
            slot.style.cssText = `position:relative;width:${100 / n}%;height:100%;overflow:hidden;-webkit-mask-image:${mask};mask-image:${mask}`;
            const img = document.createElement("img");
            img.src = src + (src.includes("?") ? "&" : "?") + "width=700";
            img.alt = "";
            img.style.cssText = n === 2
              ? "position:absolute;top:0;left:-50%;width:200%;max-width:none;height:100%;object-fit:cover"
              : `position:absolute;top:50%;left:50%;width:${100 / 0.55}%;max-width:none;height:auto;aspect-ratio:1;transform:translate(-50%,-50%)`;
            slot.appendChild(img);
            wrap.appendChild(slot);
          });
          Array.from(host.children).forEach((el) => { if (el.tagName === "IMG") el.style.visibility = "hidden"; });
          host.appendChild(wrap);
        });
      };

      perPartToggle?.addEventListener("change", () => {
        secondaryColourGroups().forEach((group) => {
          if (perPartToggle.checked) group.removeAttribute("hidden");
          else group.setAttribute("hidden", "hidden");
        });
        form.querySelectorAll("[data-secondary-default-hint]").forEach((hint) => { hint.hidden = perPartToggle.checked; });
        if (!perPartToggle.checked) syncSecondaryColoursToPrimary();
        applyVariant(findVariant());
      });

      // Picture buttons (tag "picture-options") drive their group's hidden select.
      form.addEventListener("click", (e) => {
        const tile = e.target.closest("[data-picture-value]");
        if (!tile) return;
        e.preventDefault();
        const group = tile.closest("[data-option-group]");
        group.querySelectorAll("[data-picture-value]").forEach((t) => {
          const on = t === tile;
          t.classList.toggle("is-selected", on);
          t.setAttribute("aria-pressed", on ? "true" : "false");
        });
        const hidden = group.querySelector("[data-option-selector]");
        if (hidden) hidden.value = tile.dataset.pictureValue;
        const label = group.querySelector("[data-selected-option-label]");
        if (label) label.textContent = tile.dataset.pictureValue;
        applyVariant(findVariant());
      });

      form.addEventListener("click", (e) => {
        const swatch = e.target.closest("[data-swatch-value]");
        if (!swatch) return;
        e.preventDefault();
        const group = swatch.closest("[data-option-group]");
        selectSwatchInGroup(group, swatch.dataset.swatchValue);
        if (group.hasAttribute("data-primary-colour-option")) syncSecondaryColoursToPrimary();
        applyVariant(findVariant());
      });

      form.addEventListener("change", (e) => {
        if (e.target.closest("[data-set-picker]")) updateComposite();
        if (e.target.matches('select[data-option-selector]')) {
          if (e.target === setStyleSelect) updatePerPartAvailability();
          if (e.target.closest("[data-primary-colour-option]") || e.target === setStyleSelect) syncSecondaryColoursToPrimary();
          applyVariant(findVariant());
        }
      });

      updatePerPartAvailability();
      syncSecondaryColoursToPrimary();
      applyVariant(findVariant());
    });
  }

  /* ---------------------------------------------------------------------
     Product gallery thumbnails
  --------------------------------------------------------------------- */
  function initGalleries(root = document) {
    root.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = "true";
      const main = gallery.querySelector("[data-gallery-main-image]");
      gallery.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          gallery.querySelectorAll("[data-gallery-thumb]").forEach((t) => t.classList.remove("is-active"));
          thumb.classList.add("is-active");
          if (main) {
            main.src = thumb.dataset.fullSrc || thumb.querySelector("img")?.src;
            main.srcset = "";
          }
        });
      });

      // Desktop thumb strip (mini carousel): arrows page through the row and
      // only show when there's more to scroll that way.
      const strip = gallery.querySelector("[data-gallery-thumbs]");
      const prev = gallery.querySelector("[data-gallery-strip-prev]");
      const next = gallery.querySelector("[data-gallery-strip-next]");
      if (strip && prev && next) {
        const updateArrows = () => {
          prev.hidden = strip.scrollLeft <= 2;
          next.hidden = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
        };
        prev.addEventListener("click", () => strip.scrollBy({ left: -strip.clientWidth * 0.8 }));
        next.addEventListener("click", () => strip.scrollBy({ left: strip.clientWidth * 0.8 }));
        strip.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        updateArrows();
      }

      // Mobile swipe carousel: highlight the dot matching whichever photo
      // is currently in view, so the row also works as a position indicator.
      const carousel = gallery.querySelector("[data-gallery-carousel]");
      const dots = gallery.querySelectorAll("[data-gallery-dot]");
      if (carousel && dots.length) {
        let ticking = false;
        carousel.addEventListener("scroll", () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
            dots.forEach((dot, i) => dot.classList.toggle("is-active", i === index));
            ticking = false;
          });
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
     Single-open accordion (product info sections)
  --------------------------------------------------------------------- */
  function initAccordions(root = document) {
    root.querySelectorAll("[data-accordion-group]").forEach((group) => {
      if (group.dataset.bound) return;
      group.dataset.bound = "true";
      const items = Array.from(group.querySelectorAll("details"));
      items.forEach((item) => {
        item.addEventListener("toggle", () => {
          if (item.open) items.filter((i) => i !== item).forEach((i) => (i.open = false));
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Collection: mobile filter drawer toggle
  --------------------------------------------------------------------- */
  function initFilterToggle(root = document) {
    root.querySelectorAll("[data-filter-toggle]").forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = "true";
      btn.addEventListener("click", () => {
        const panel = document.querySelector(btn.dataset.filterToggle);
        const open = panel.hasAttribute("hidden");
        if (open) panel.removeAttribute("hidden");
        else panel.setAttribute("hidden", "hidden");
        btn.setAttribute("aria-expanded", String(open));
      });
    });
  }

  /* ---------------------------------------------------------------------
     Sticky add-to-cart bar
  --------------------------------------------------------------------- */
  function initStickyCartBar() {
    const bar = document.querySelector("[data-sticky-cart-bar]");
    const anchor = document.querySelector("[data-add-to-cart-anchor]");
    if (!bar || !anchor) return;
    // Only once the main button has scrolled up past the top: while it is
    // still below the fold the bar would just cover the product photos.
    // A scroll check (not an IntersectionObserver) so a fast flick or a reload
    // halfway down the page, which never crosses the button, still shows it.
    let ticking = false;
    const update = () => {
      bar.classList.toggle("is-visible", anchor.getBoundingClientRect().bottom < 0);
      ticking = false;
    };
    window.addEventListener("scroll", () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------------------------------------------------------------------
     Reviews carousel
  --------------------------------------------------------------------- */
  function initReviewCarousels(root = document) {
    root.querySelectorAll("[data-review-carousel]").forEach((carousel) => {
      if (carousel.dataset.bound) return;
      carousel.dataset.bound = "true";
      const track = carousel.querySelector(".reviews-track__inner");
      const cards = Array.from(carousel.querySelectorAll(".review-card"));
      const dots = Array.from(carousel.querySelectorAll("[data-review-dot]"));
      let index = 0;
      const cardWidth = () => (cards[0]?.getBoundingClientRect().width || 320) + 20;

      const update = () => {
        track.style.transform = `translateX(-${index * cardWidth()}px)`;
        dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
      };
      carousel.querySelector("[data-review-prev]")?.addEventListener("click", () => {
        index = Math.max(0, index - 1);
        update();
      });
      carousel.querySelector("[data-review-next]")?.addEventListener("click", () => {
        index = Math.min(cards.length - 1, index + 1);
        update();
      });
      dots.forEach((dot, i) =>
        dot.addEventListener("click", () => {
          index = i;
          update();
        })
      );
      window.addEventListener("resize", update);
    });
  }

  /* ---------------------------------------------------------------------
     Product recommendations (Shopify's native recommendation engine)
  --------------------------------------------------------------------- */
  function initProductRecommendations() {
    document.querySelectorAll("[data-product-recommendations]").forEach(async (el) => {
      const url = el.dataset.url;
      if (!url) return;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const content = doc.querySelector("[data-product-recommendations]");
        if (content && content.innerHTML.trim()) el.innerHTML = content.innerHTML;
      } catch (err) {
        /* leave server-rendered fallback in place */
      }
    });
  }

  /* ---------------------------------------------------------------------
     Predictive search — type-ahead results via Shopify's native
     /search/suggest.json endpoint, no page navigation while typing.
     Pressing Enter or clicking "See all results" still submits the real
     <form>, so it works the same with JavaScript off.
  --------------------------------------------------------------------- */
  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str == null ? "" : str;
    return div.innerHTML;
  }

  function initPredictiveSearch() {
    const root = document.querySelector("[data-predictive-search]");
    if (!root) return;
    const toggle = root.querySelector("[data-predictive-search-toggle]");
    const panel = root.querySelector("[data-predictive-search-panel]");
    const input = root.querySelector("[data-predictive-search-input]");
    const closeBtn = root.querySelector("[data-predictive-search-close]");
    const results = root.querySelector("[data-predictive-search-results]");
    let debounceTimer;
    let currentController;

    const open = () => {
      panel.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
      setTimeout(() => input.focus(), 30);
    };
    const close = () => {
      panel.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
    };

    toggle.addEventListener("click", () => (panel.hidden ? open() : close()));
    closeBtn?.addEventListener("click", close);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !panel.hidden) close();
    });
    document.addEventListener("click", (e) => {
      if (!panel.hidden && !root.contains(e.target)) close();
    });

    const renderResults = (query, products) => {
      if (!products.length) {
        results.innerHTML = `<p class="predictive-search__status">${escapeHtml(
          root.dataset.noResultsText || "No results"
        )}</p>`;
        return;
      }
      const items = products
        .map(
          (p) => `
        <a href="${p.url}" class="predictive-search__item">
          <span class="predictive-search__item-media">${
            p.image ? `<img src="${p.image}" alt="" loading="lazy">` : ""
          }</span>
          <span class="predictive-search__item-body">
            <span class="predictive-search__item-title">${escapeHtml(p.title)}</span>
            <span class="predictive-search__item-price">${p.price}</span>
          </span>
        </a>`
        )
        .join("");
      const viewAllHref = `/search?q=${encodeURIComponent(query)}&type=product`;
      results.innerHTML = `${items}<a href="${viewAllHref}" class="predictive-search__view-all">See all results for "${escapeHtml(
        query
      )}"</a>`;
    };

    const fetchResults = async (query) => {
      results.innerHTML = `<p class="predictive-search__status">Searching…</p>`;
      currentController?.abort();
      currentController = new AbortController();
      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(
            query
          )}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`,
          { signal: currentController.signal }
        );
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        renderResults(query, products);
      } catch (err) {
        if (err.name !== "AbortError") results.innerHTML = "";
      }
    };

    input.addEventListener("input", () => {
      const query = input.value.trim();
      clearTimeout(debounceTimer);
      if (query.length < 2) {
        results.innerHTML = "";
        return;
      }
      debounceTimer = setTimeout(() => fetchResults(query), 250);
    });
  }

  /* ---------------------------------------------------------------------
     Boot
  --------------------------------------------------------------------- */
  document.addEventListener("DOMContentLoaded", () => {
    window.cankoCart = new CankoCart();
    initQuantitySteppers();
    initVariantPickers();
    initGalleries();
    initAccordions();
    initFilterToggle();
    initStickyCartBar();
    initReviewCarousels();
    initProductRecommendations();
    initPredictiveSearch();
  });
})();
